import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Shield, Fingerprint, Lock, Users, Building, Eye, ArrowRight, Search, Trash2, AlertTriangle, ShieldCheck, Radar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ICON_MAP = {
  Shield,
  Fingerprint,
  Lock,
  Users,
  Building,
  Eye,
  Search,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  Radar,
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.06, ease: "easeOut" },
  }),
};

export default function SolutionsSection({ data, registerSection }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) registerSection("solutions", ref.current);
  }, [registerSection]);

  if (!data) return null;

  const handleScrollTo = (e, target) => {
    e.preventDefault();
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      id="solutions"
      className="py-16 md:py-24 relative"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="max-w-2xl mb-12 md:mb-16"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
            What We Do
          </p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-[-0.01em] text-foreground">
            {data.section_title}
          </h2>
          <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
            {data.section_subtitle}
          </p>
        </motion.div>

        {/* Services Grid - adaptive for 5 services */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {data.services?.map((service, i) => {
            const IconComponent = ICON_MAP[service.icon] || Shield;
            const isFeatured = service.featured;
            const isEmergency = service.id === "emergency-triage";
            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                className={isFeatured ? "lg:row-span-2" : ""}
              >
                <Card
                  data-testid="solutions-service-card"
                  className={`rounded-2xl border p-6 md:p-8 h-full card-hover flex flex-col ${
                    isFeatured
                      ? "bg-[hsl(var(--brand-slate))] text-[hsl(var(--brand-ivory))] border-transparent"
                      : isEmergency
                      ? "bg-card border-[#C0392B]/30"
                      : "bg-card border-border"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isFeatured
                          ? "bg-white/10"
                          : isEmergency
                          ? "bg-[#C0392B]/10 border-2 border-[#C0392B]"
                          : "bg-accent"
                      }`}
                    >
                      <IconComponent
                        className={`h-5 w-5 ${
                          isFeatured
                            ? "text-[hsl(var(--brand-brass-2))]"
                            : isEmergency
                            ? "text-[#C0392B]"
                            : "text-foreground"
                        }`}
                        aria-hidden="true"
                      />
                    </div>
                    <Badge
                      variant={isFeatured ? "outline" : "secondary"}
                      className={`text-[10px] uppercase tracking-[0.12em] font-mono ${
                        isFeatured
                          ? "border-white/20 text-[hsl(var(--brand-brass-2))]"
                          : isEmergency
                          ? "bg-[#C0392B]/10 text-[#C0392B] border border-[#C0392B]"
                          : ""
                      }`}
                    >
                      {service.eyebrow}
                    </Badge>
                  </div>

                  <h3
                    className={`font-heading text-lg md:text-xl mb-2 ${
                      isFeatured
                        ? "text-[hsl(var(--brand-ivory))]"
                        : isEmergency
                        ? "text-[#C0392B]"
                        : "text-foreground"
                    }`}
                  >
                    {service.title}
                  </h3>

                  <p
                    className={`text-sm leading-relaxed mb-5 ${
                      isFeatured ? "text-white/70" : "text-muted-foreground"
                    }`}
                  >
                    {service.description}
                  </p>

                  <ul className="space-y-2.5 mb-5 flex-grow">
                    {service.features?.map((feature, fi) => (
                      <li
                        key={fi}
                        className={`text-xs leading-relaxed flex items-start gap-2.5 ${
                          isFeatured ? "text-white/60" : "text-muted-foreground"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${
                            isFeatured
                              ? "bg-[hsl(var(--brand-brass-2))]"
                              : "bg-[hsl(var(--brand-brass))]"
                          }`}
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#pricing"
                    data-testid="solutions-learn-more-link"
                    onClick={(e) => handleScrollTo(e, "#pricing")}
                    className={`text-sm font-medium inline-flex items-center gap-1 mt-auto ${
                      isFeatured
                        ? "text-[hsl(var(--brand-brass-2))] hover:text-[hsl(var(--brand-brass-2))]/80"
                        : "text-foreground hover:text-foreground/70"
                    }`}
                  >
                    Learn more
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Band */}
        <motion.div
          className="mt-12 md:mt-16 rounded-2xl bg-accent/50 border border-border p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <p className="text-sm md:text-base text-foreground font-medium">
            {data.cta_text}
          </p>
          <Button
            className="h-11 px-5 text-sm rounded-xl bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 btn-primary-hover whitespace-nowrap"
            data-testid="solutions-cta-button"
            onClick={(e) => handleScrollTo(e, "#pricing")}
          >
            {data.cta_button}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
